import { useEffect, useState } from 'react';
import { UserPlus, Users, DollarSign, Percent } from 'lucide-react';
import { referralsAPI } from '../lib/api';
import { formatCurrency, formatNumber, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';

export default function Referrals() {
  const [data, setData] = useState(null);

  useEffect(() => {
    referralsAPI.overview()
      .then((res) => setData(res.data))
      .catch(() => toast.error('Failed to load referrals'));
  }, []);

  const top = data?.top || [];

  return (
    <div data-testid="referrals-page">
      <div className="page-header">
        <h1 className="page-title">Referrals</h1>
        <p className="page-subtitle">Codes and signups from live player accounts</p>
      </div>

      <div className="widget-grid mb-6">
        <div className="stat-card">
          <Users className="w-5 h-5 text-primary mb-2" />
          <p className="stat-value">{formatNumber(data?.total_players || 0)}</p>
          <p className="stat-label">Players with a code</p>
        </div>
        <div className="stat-card">
          <UserPlus className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="stat-value">{formatNumber(data?.total_referrals || 0)}</p>
          <p className="stat-label">Joined with a code</p>
        </div>
        <div className="stat-card">
          <DollarSign className="w-5 h-5 text-amber-400 mb-2" />
          <p className="stat-value">{formatCurrency(data?.bonus_paid || 0)}</p>
          <p className="stat-label">Referral bonus paid</p>
        </div>
        <div className="stat-card">
          <Percent className="w-5 h-5 text-blue-400 mb-2" />
          <p className="stat-value">{((data?.conversion_rate || 0) * 100).toFixed(0)}%</p>
          <p className="stat-label">Referred players who deposited</p>
        </div>
      </div>

      <div className="data-table-container mb-6">
        <div className="data-table-header">
          <h3 className="data-table-title">{data?.total_referrals ? 'Players who referred others' : 'Player referral codes'}</h3>
        </div>
        <div className="p-6">
          {top.length === 0 ? (
            <p className="text-sm text-muted-foreground">No players yet.</p>
          ) : (
            <div className="space-y-4">
              {top.map((user, idx) => (
                <div key={user.user_id || user.referral_code || idx} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold ${idx === 0 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                      {idx + 1}
                    </div>
                    <div>
                      <p className="font-medium">{user.name || user.phone}</p>
                      <p className="text-sm text-muted-foreground">{user.phone} · {user.referral_code} · {user.referrals} referrals</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="data-table-container">
        <div className="data-table-header">
          <h3 className="data-table-title">Recent referred signups</h3>
        </div>
        <div className="p-6 space-y-3">
          {(data?.recent || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No player has signed up with another player’s code yet.</p>
          ) : data.recent.map((row, idx) => (
            <div key={`${row.phone}-${idx}`} className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium">{row.name}</p>
                <p className="text-sm text-muted-foreground">Used code {row.referred_by}</p>
              </div>
              <span className="text-sm text-muted-foreground">{formatDateTime(row.created_at)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
